import { cn } from '@/lib/utils';

/**
 * MARKA İŞARETİ
 *
 * Mağazanın gerçek logosu kullanılır: `public/logo.png` (daire, saydam
 * zeminli PNG — kağıt/koyu her zeminde aynı durur).
 *
 * `logoUrl` verilirse onun yerine o adres render edilir; ayarlardan logo
 * yüklendiğinde tasarım değişikliği gerekmez. `logoUrl={null}` verilirse
 * ilk sürümdeki tipografik wordmark'a düşülür (PRODUCT-DECISIONS §1.6).
 */

/** Marka logosu; public/ altındadır, bundler'a değil sunucuya bağlıdır. */
export const BRAND_LOGO_URL = '/logo.png';

interface WordmarkProps {
  logoUrl?: string | null;
  storeName?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Koyu fotoğraf üzerinde kullanım */
  inverted?: boolean;
  className?: string;
}

const sizes = {
  sm: { primary: 'text-lg', secondary: 'text-[9px]', gap: 'mt-0.5' },
  md: { primary: 'text-2xl', secondary: 'text-[10px]', gap: 'mt-1' },
  lg: { primary: 'text-4xl', secondary: 'text-2xs', gap: 'mt-1.5' },
} as const;

/**
 * Logo yuvarlaktır ve marka adını İÇİNDE taşır; tipografik wordmark'tan
 * daha büyük render edilmezse "HAYAL ÇİÇEK" yazısı okunmaz.
 * `sm` başlık çubuğu içindir (60px başlıkta 44px logo).
 */
const logoHeights = { sm: 'h-11', md: 'h-14', lg: 'h-24' } as const;

export function Wordmark({
  logoUrl = BRAND_LOGO_URL,
  storeName = 'Hayal Çiçek & Hediyelik',
  size = 'md',
  inverted = false,
  className,
}: WordmarkProps) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={storeName}
        className={cn('w-auto object-contain', logoHeights[size], className)}
      />
    );
  }

  const scale = sizes[size];

  return (
    <span
      className={cn('inline-flex flex-col items-center leading-none', className)}
      aria-label={storeName}
      role="img"
    >
      <span
        className={cn(
          'font-serif font-normal tracking-[0.16em]',
          scale.primary,
          inverted ? 'text-white' : 'text-ink',
        )}
      >
        HAYAL
      </span>

      <span
        className={cn(
          'flex items-center gap-2 uppercase tracking-[0.34em]',
          scale.gap,
          scale.secondary,
          inverted ? 'text-white/70' : 'text-ink-muted',
        )}
      >
        {/* Altın yalnızca 1px çizgi olarak kullanılır — dolu alan asla. */}
        <span
          aria-hidden
          className={cn(
            'h-px w-3',
            inverted ? 'bg-white/40' : 'bg-gold',
          )}
        />
        Çiçek &amp; Hediyelik
        <span
          aria-hidden
          className={cn('h-px w-3', inverted ? 'bg-white/40' : 'bg-gold')}
        />
      </span>
    </span>
  );
}
